import React, { useCallback, useState } from "react";
import isEmpty from "lodash.isempty";
import uniqueId from "lodash.uniqueid";

import { HvInput } from "@hitachivantara/uikit-react-core";

import Context from "../Context";
import { useStyles } from "./styles";
import { NumericRange, ValueComponentProps } from "../types";

interface NumericValueProps extends ValueComponentProps {
  operator: string;
  value?: number | NumericRange;
}

type validationTypes = "required" | "invalid" | "equal" | "greaterThan" | null;

const NumericValue = ({
  id,
  value,
  operator,
  initialTouched = false,
}: NumericValueProps): JSX.Element => {
  const classes = useStyles();
  const isRange = operator === "range";
  const context = React.useContext(Context);
  const { labels, dispatchAction } = context;

  const onSingleValueChange = useCallback(
    (evt, data) => {
      const numericData = isEmpty(data) ? null : Number(data);
      dispatchAction({
        type: "set-value",
        id,
        value: Number.isNaN(numericData) ? data : numericData,
      });
    },
    [dispatchAction, id]
  );

  const onRangeValueChange = useCallback(
    (evt, data, from = true) => {
      const numericData = isEmpty(data) ? null : Number(data);
      const currentValue = value as NumericRange;
      const numericRange = {
        from: currentValue?.from,
        to: currentValue?.to,
      } as NumericRange;
      if (from) {
        numericRange.from = Number.isNaN(numericData) ? data : numericData;
      } else {
        numericRange.to = Number.isNaN(numericData) ? data : numericData;
      }
      dispatchAction({
        type: "set-value",
        id,
        value: numericRange,
      });
    },
    [dispatchAction, id, value]
  );

  const [touchedNumeric, setTouchedNumeric] = useState(initialTouched);
  const [touchedNumericTo, setTouchedNumericTo] = useState(initialTouched);

  const elementId = uniqueId("numeric");

  let numericValidation: validationTypes = null;
  let rightValidation: validationTypes = null;
  if (touchedNumeric || touchedNumericTo) {
    if (value === undefined || value?.toString() === "") {
      if (touchedNumeric) {
        numericValidation = "required";
      }
      if (touchedNumericTo) {
        rightValidation = "required";
      }
    } else if (!isRange) {
      if (Number.isNaN(Number(value))) {
        numericValidation = "invalid";
      }
    } else if (isRange) {
      const rangeValue = value as NumericRange;
      if (
        rangeValue?.from === undefined ||
        rangeValue?.from?.toString() === ""
      ) {
        numericValidation = "required";
      } else if (Number.isNaN(Number(rangeValue?.from))) {
        numericValidation = "invalid";
      }

      if (rangeValue?.to === undefined || rangeValue?.to?.toString() === "") {
        rightValidation = "required";
      } else if (Number.isNaN(Number(rangeValue?.to))) {
        rightValidation = "invalid";
      } else if (Number(rangeValue?.from) > Number(rangeValue?.to)) {
        rightValidation = "greaterThan";
      } else if (Number(rangeValue?.from) === Number(rangeValue?.to)) {
        rightValidation = "equal";
      }
    }
  }

  const renderRangeInputs = (rangeValue: NumericRange) => (
    <div className={classes.rangeContainer}>
      <div className={classes.inputContainer}>
        <HvInput
          label={labels.rule.value.numeric.range.leftLabel}
          className={classes.input}
          id={`${elementId}-numeric-from`}
          name={`${elementId}-numeric-from`}
          value={rangeValue?.from?.toString() || ""}
          onChange={(event, data) => onRangeValueChange(event, data)}
          onBlur={() => {
            setTouchedNumeric(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
          status={
            !touchedNumeric
              ? "standBy"
              : numericValidation != null
              ? "invalid"
              : "valid"
          }
          statusMessage={
            labels.rule.value.numeric.validation[
              numericValidation as "required" | "invalid"
            ] || ""
          }
          required
          inputProps={{
            autoComplete: "off",
          }}
          placeholder={labels.rule.value.numeric.placeholder}
        />
      </div>
      <div className={classes.inputContainer}>
        <HvInput
          label={labels.rule.value.numeric.range.rightLabel}
          className={classes.input}
          id={`${elementId}-numeric-to`}
          name={`${elementId}-numeric-to`}
          value={rangeValue?.to?.toString() || ""}
          onChange={(event, data) => onRangeValueChange(event, data, false)}
          onBlur={() => {
            setTouchedNumericTo(true);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
          status={
            !touchedNumericTo
              ? "standBy"
              : rightValidation != null
              ? "invalid"
              : "valid"
          }
          statusMessage={
            labels.rule.value.numeric.validation[
              rightValidation as
                | "required"
                | "invalid"
                | "equal"
                | "greaterThan"
            ] || ""
          }
          required
          inputProps={{
            autoComplete: "off",
          }}
          placeholder={labels.rule.value.numeric.placeholder}
        />
      </div>
    </div>
  );

  return (
    <div className={classes.root}>
      {isRange && renderRangeInputs((value as NumericRange) || {})}
      {!isRange && (
        <div className={classes.inputContainer}>
          <HvInput
            label={labels.rule.value.numeric.label}
            className={classes.input}
            id={`${elementId}-numeric`}
            name={`${elementId}-numeric`}
            value={value || ""}
            onChange={onSingleValueChange}
            onBlur={() => {
              setTouchedNumeric(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
              }
            }}
            status={
              !touchedNumeric
                ? "standBy"
                : numericValidation != null
                ? "invalid"
                : "valid"
            }
            required
            inputProps={{
              autoComplete: "off",
            }}
            placeholder={labels.rule.value.numeric.placeholder}
            statusMessage={
              labels.rule.value.numeric.validation[
                numericValidation as "required" | "invalid"
              ] || ""
            }
          />
        </div>
      )}
    </div>
  );
};

export default React.memo(NumericValue);
